/**
 * Ticket Turno - Printable turn ticket component
 */

import { forwardRef } from "react";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import type { Turno } from "@/hooks/use-turnos";

interface TicketTurnoProps {
  turno: Turno;
  sucursalNombre?: string;
}

export const TicketTurno = forwardRef<HTMLDivElement, TicketTurnoProps>(
  ({ turno, sucursalNombre }, ref) => {
    const { t } = useTranslation();

    const formatTime = (time: string) => {
      const [hours, minutes] = time.split(":");
      return `${hours}:${minutes}`;
    };

    return (
      <div
        ref={ref}
        className="w-[280px] bg-white p-4 font-mono text-black print:shadow-none"
        style={{ fontFamily: "monospace" }}
      >
        {/* Header */}
        <div className="text-center border-b-2 border-dashed border-gray-400 pb-3 mb-3">
          <h1 className="text-lg font-bold">Laboratorio Molina Valdez</h1>
          {sucursalNombre && <p className="text-sm">{sucursalNombre}</p>}
          <p className="text-xs text-gray-600 mt-1">
            {format(new Date(turno.fecha), "EEEE, d 'de' MMMM 'de' yyyy", {
              locale: es,
            })}
          </p>
        </div>

        {/* Turn Number */}
        <div className="text-center py-4">
          <p className="text-sm text-gray-600 uppercase">{t("turnos.yourTurn", "Su turno")}</p>
          <p className="text-6xl font-bold my-2">{String(turno.numero).padStart(3, "0")}</p>
        </div>

        {/* Details */}
        <div className="border-t-2 border-dashed border-gray-400 pt-3 mt-3 space-y-1 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-600">{t("turnos.arrivalTime", "Hora llegada")}:</span>
            <span className="font-semibold">{formatTime(turno.hora_llegada)}</span>
          </div>
          {turno.tipo_servicio && (
            <div className="flex justify-between">
              <span className="text-gray-600">{t("turnos.service", "Servicio")}:</span>
              <span className="font-semibold capitalize">{turno.tipo_servicio}</span>
            </div>
          )}
        </div>

        {/* Estimated Wait */}
        {turno.tiempo_espera !== null && (
          <div className="text-center mt-4 py-2 bg-gray-100 rounded">
            <p className="text-xs text-gray-600">
              {t("turnos.estimatedWait", "Tiempo estimado de espera")}
            </p>
            <p className="text-lg font-bold">
              ~{turno.tiempo_espera} {t("turnos.minutes", "min")}
            </p>
          </div>
        )}

        {/* Footer */}
        <div className="text-center mt-4 pt-3 border-t-2 border-dashed border-gray-400">
          <p className="text-xs text-gray-500">
            {t("turnos.waitMessage", "Por favor espere a ser llamado")}
          </p>
          <p className="text-xs text-gray-400 mt-2">{format(new Date(), "HH:mm:ss")}</p>
        </div>
      </div>
    );
  }
);

TicketTurno.displayName = "TicketTurno";
